import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { IStore } from '../../interfaces';
import * as actions from '../../actions';
import { useClickOutside } from '../../utils/useClickOutside';
import { IDetailTrophyTooltip, OrderByDetailTrophyTooltip } from '../../interfaces/clientInterfaces';
import { useAppDispatch } from '../../utils/modules';

const DetailTrophyTooltipContainer = styled.div`
	margin: auto;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	width: 85%;
	display: inline-table;
	max-height: 70%;
	border-radius: 4px;
	border: 1px  solid #F89504;
	color: white;
	text-align: center;
	text-shadow: 2px 2px 2px gold;
	background-color: black;
	padding: 8px;
	user-select: none;
	z-index: 1111114;
	p {
		margin: 5px 0 0 0;
	}
`;

interface IImage {
	isRunnerUp: boolean;
	isMobileViewActive: boolean;
}
const Image = styled.img<IImage>`
	max-height: ${props => props.isMobileViewActive ? '56px' : '65px'};
	object-fit: contain;
	filter: ${props => props.isRunnerUp ? 'opacity(0.5)' : 'drop-shadow(1px 4px 4px gold)'};
`;

interface IImagesContainer {
	isMobileViewActive: boolean;
}
const ImagesContainer = styled.div<IImagesContainer>`
	overflow-y: auto;
	flex-wrap: wrap;
	display: flex;
	max-height: ${props => props.isMobileViewActive ? 450 : 700}px;
	overflow-y: auto;
	padding: 10px;
`;

const Title = styled.div`
	font-size: 16px;
	margin: 5px auto;
`;

const ImageContainer = styled.div`
	display: flex;
	flex-direction: column;
	padding: 3px;
	margin: auto;
`;

const Year = styled.div`
	flex: 0;
	font-size: 12px;
`;

const SortIcon = styled.i`
	font-size: 18px;
	color: #F89504;
	padding: 5px;
	text-shadow: none;
	cursor: pointer;
`;

const CloseIcon = styled.i`
	font-size: 14px;
	color: #fff;
	padding: 5px;
	text-shadow: none;
	cursor: pointer;
	position: absolute;
	right: 0;
	top: 0px;
	margin: 3px; 3px 0 0;
`;

//#endregion


interface IDetailTrophyTooltipProps {
	readonly detailTrophyTooltip: IDetailTrophyTooltip;
}
export default function DetailTrophyTooltip({
	detailTrophyTooltip,
}: IDetailTrophyTooltipProps) {

	const dispatch = useAppDispatch();

	const containerRef = useRef<HTMLDivElement>(null);

	const handleClickOutside = () => {
		dispatch(actions.hideDetailTrophyTooltip());
	};

	useClickOutside(containerRef, handleClickOutside);

	const isMobileViewActive = useSelector(
		(completeState: IStore): boolean => { 
			return completeState?.ui?.isMobileViewActive ?? false;
		});

	const [orderBy, setOrderBy] = useState(detailTrophyTooltip.orderBy);
	const [showCompleteRunnerUps, setShowCompleteRunnerUps] = useState(false);
	const [detailTrophies, setDetailTrophies] = useState(
		detailTrophyTooltip.orderBy === OrderByDetailTrophyTooltip.competition
			? detailTrophyTooltip.detailTrophiesByCompetition
			: detailTrophyTooltip.detailTrophiesByYearEnd
	);

	const sortIconClick = useCallback(
		() => {
			if (orderBy === OrderByDetailTrophyTooltip.chronologically) {
				setOrderBy(OrderByDetailTrophyTooltip.competition);
				setShowCompleteRunnerUps(false);
				setDetailTrophies(detailTrophyTooltip.detailTrophiesByCompetition);
			} else {
				setOrderBy(OrderByDetailTrophyTooltip.chronologically);
				setShowCompleteRunnerUps(false);
				setDetailTrophies(detailTrophyTooltip.detailTrophiesByYearEnd);
			}
		},
		[detailTrophyTooltip.detailTrophiesByCompetition, detailTrophyTooltip.detailTrophiesByYearEnd, orderBy]
	);

	const runnerUpIconClick = useCallback(
		() => {
			if (orderBy === OrderByDetailTrophyTooltip.chronologically) {
				setOrderBy(OrderByDetailTrophyTooltip.competition);
				setShowCompleteRunnerUps(false);
				setDetailTrophies(detailTrophyTooltip.detailTrophiesByCompetition);
			} else {
				setOrderBy(OrderByDetailTrophyTooltip.chronologically);
				setShowCompleteRunnerUps(false);
				setDetailTrophies(detailTrophyTooltip.detailTrophiesByYearEnd);
			}
			const editedShowCompleteRunnerUps = !showCompleteRunnerUps;
			if (editedShowCompleteRunnerUps) {
				setShowCompleteRunnerUps(editedShowCompleteRunnerUps);
				setDetailTrophies(orderBy === OrderByDetailTrophyTooltip.competition 
					? detailTrophyTooltip.completeDetailTrophiesByCompetition
					: detailTrophyTooltip.completeDetailTrophiesByYearEnd);
			} else {
				setShowCompleteRunnerUps(editedShowCompleteRunnerUps);
				setDetailTrophies(orderBy === OrderByDetailTrophyTooltip.competition 
					? detailTrophyTooltip.completeDetailTrophiesByCompetition
					: detailTrophyTooltip.completeDetailTrophiesByYearEnd);
			}
		},
		[detailTrophyTooltip.completeDetailTrophiesByCompetition, detailTrophyTooltip.completeDetailTrophiesByYearEnd, detailTrophyTooltip.detailTrophiesByCompetition, detailTrophyTooltip.detailTrophiesByYearEnd, orderBy, showCompleteRunnerUps]
	);

	return (
		<DetailTrophyTooltipContainer ref={containerRef}>
			<Title>
				{detailTrophyTooltip.title}
				{
					<>
						<SortIcon
							className={orderBy === OrderByDetailTrophyTooltip.chronologically ? 'fa fa-clock' : 'fa fa-trophy'}
							title={`Click to order ${orderBy === OrderByDetailTrophyTooltip.chronologically ? 'by competition' : 'chronologically'}`}
							onClick={sortIconClick}
						/>
						{
							orderBy === OrderByDetailTrophyTooltip.chronologically
								? <SortIcon
									className={showCompleteRunnerUps ? 'fa fa-list' : 'fa fa-award'}
									title={`Click to show ${showCompleteRunnerUps ? 'only relevant' : 'complete'} runner ups`}
									onClick={runnerUpIconClick}
								/>
								: null
						}
					</>
				}
				<CloseIcon
					className={'fa fa-close'}
					title={'Close'}
					onClick={handleClickOutside}
				/>
			</Title>
			<ImagesContainer isMobileViewActive={isMobileViewActive}>
				{detailTrophies.map(detailTrophy => {
					return (
						<ImageContainer>
							<Year>{detailTrophy.yearEndText}</Year>
							<Image
								title={`${detailTrophy.isRunnerUp ? 'Runner up ' : ''}${detailTrophy.text}`}
								src={`./../../img/trophy/${detailTrophy.trophy}.png`}
								isRunnerUp={detailTrophy.isRunnerUp}
								isMobileViewActive={isMobileViewActive}
							/>
						</ImageContainer>
					);
				})}
			</ImagesContainer>
		</DetailTrophyTooltipContainer>
	);
}
